import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Switch,
  ActivityIndicator,
  StyleSheet,
  Image,
  Platform,
} from 'react-native';
import { useAdminThemeContext } from '../contexts/AdminThemeContext';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  listAllMutualFunds,
  upsertMutualFund,
  setMutualFundActive,
} from '../utils/userFundsService';
import { formatFundsCents } from '../constants/userFunds';
import {
  subscribeMutualAidPage,
  subscribeMutualAidSuggestions,
  uploadFundPageHero,
  saveMutualAidPageHero,
  saveMutualAidVotingPeriod,
  setMutualAidSuggestionStatus,
  selectMutualAidWinner,
  normalizeSuggestionStatus,
  summarizeMutualAidAdmin,
  suggestionDisplayFields,
} from '../utils/mutualAidFundService';

async function uploadMutualFundCover(file) {
  if (!file) return null;
  const safeName = String(file.name || 'cover.jpg').replace(/[^\w.\-]+/g, '_');
  const path = `mutual_funds/covers/${Date.now()}_${safeName}`;
  const ref = storageRef(getStorage(), path);
  const contentType = file.type || 'image/jpeg';
  await uploadBytes(ref, file, { contentType });
  return getDownloadURL(ref);
}

/**
 * Admin Mutual Aid Fund manager — activate/deactivate causes shown on the Fund page. Credits are separate.
 */
function switchColors(colors, isDarkMode) {
  return {
    trackColor: {
      false: isDarkMode ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.18)',
      true: colors.accentGreen,
    },
    thumbColor: isDarkMode ? '#f4f4f5' : '#ffffff',
    ios_backgroundColor: isDarkMode ? 'rgba(255,255,255,0.16)' : '#d1d1d6',
  };
}

export default function AdminMutualFunds({ onNotify }) {
  const { colors, isDarkMode } = useAdminThemeContext();
  const styles = useMemo(() => createMutualFundAdminStyles(colors, isDarkMode), [colors, isDarkMode]);
  const toggleColors = useMemo(() => switchColors(colors, isDarkMode), [colors, isDarkMode]);
  const fileInputRef = useRef(null);
  const pageHeroInputRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageHeroUrl, setPageHeroUrl] = useState('');
  const [pageHeroFile, setPageHeroFile] = useState(null);
  const [pageHeroPreview, setPageHeroPreview] = useState('');
  const [pageHeroSaving, setPageHeroSaving] = useState(false);
  const [page, setPage] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [votingOpen, setVotingOpen] = useState(false);
  const [votingEndsAt, setVotingEndsAt] = useState('');
  const [reviewBusy, setReviewBusy] = useState(null);
  const [draft, setDraft] = useState({
    title: '',
    description: '',
    goalDollars: '500',
    active: true,
    coverImageUrl: '',
    coverFile: null,
    coverPreview: '',
  });

  const reload = async () => {
    setLoading(true);
    try {
      setRows(await listAllMutualFunds());
    } catch (err) {
      onNotify?.(err?.message || 'Failed to load mutual funds', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  useEffect(() => {
    return subscribeMutualAidPage(
      (data) => {
        const next = data || {};
        setPage(next);
        setPageHeroUrl(String(next.heroImageUrl || ''));
        setVotingOpen(Boolean(next.votingOpen));
        setVotingEndsAt(String(next.votingEndsAt || ''));
      },
      (err) => onNotify?.(err?.message || 'Could not load Fund page photo', 'error'),
    );
  }, []);

  useEffect(() => {
    return subscribeMutualAidSuggestions(
      setSuggestions,
      (err) => onNotify?.(err?.message || 'Could not load suggestions', 'error'),
    );
  }, []);

  useEffect(() => () => {
    if (draft.coverPreview?.startsWith?.('blob:')) {
      try { URL.revokeObjectURL(draft.coverPreview); } catch (_) {}
    }
  }, [draft.coverPreview]);

  const pickCover = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    if (draft.coverPreview?.startsWith?.('blob:')) {
      try { URL.revokeObjectURL(draft.coverPreview); } catch (_) {}
    }
    const preview = URL.createObjectURL(file);
    setDraft((p) => ({
      ...p,
      coverFile: file,
      coverPreview: preview,
    }));
    if (event?.target) event.target.value = '';
  };

  const clearCover = () => {
    if (draft.coverPreview?.startsWith?.('blob:')) {
      try { URL.revokeObjectURL(draft.coverPreview); } catch (_) {}
    }
    setDraft((p) => ({
      ...p,
      coverFile: null,
      coverPreview: '',
      coverImageUrl: '',
    }));
  };

  const createFund = async () => {
    setSaving(true);
    try {
      let coverImageUrl = draft.coverImageUrl || null;
      if (draft.coverFile) {
        coverImageUrl = await uploadMutualFundCover(draft.coverFile);
      }
      await upsertMutualFund({
        title: draft.title,
        description: draft.description,
        goalCents: Math.round(Number(draft.goalDollars || 0) * 100),
        active: draft.active,
        sortOrder: rows.length,
        coverImageUrl,
      });
      if (draft.coverPreview?.startsWith?.('blob:')) {
        try { URL.revokeObjectURL(draft.coverPreview); } catch (_) {}
      }
      setDraft({
        title: '',
        description: '',
        goalDollars: '500',
        active: true,
        coverImageUrl: '',
        coverFile: null,
        coverPreview: '',
      });
      onNotify?.('Mutual fund created', 'success');
      await reload();
    } catch (err) {
      onNotify?.(err?.message || 'Could not create fund', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (fund) => {
    try {
      await setMutualFundActive(fund.id, !fund.active);
      await reload();
    } catch (err) {
      onNotify?.(err?.message || 'Could not update fund', 'error');
    }
  };

  const pickPageHero = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    if (event?.target) event.target.value = '';
    if (pageHeroPreview?.startsWith?.('blob:')) {
      try { URL.revokeObjectURL(pageHeroPreview); } catch (_) {}
    }
    const preview = URL.createObjectURL(file);
    setPageHeroFile(file);
    setPageHeroPreview(preview);
    setPageHeroSaving(true);
    try {
      const url = await uploadFundPageHero(file);
      await saveMutualAidPageHero(url);
      try { URL.revokeObjectURL(preview); } catch (_) {}
      setPageHeroFile(null);
      setPageHeroPreview('');
      setPageHeroUrl(url);
      onNotify?.('Fund page photo saved', 'success');
    } catch (err) {
      onNotify?.(err?.message || 'Could not save Fund page photo', 'error');
    } finally {
      setPageHeroSaving(false);
    }
  };

  const clearPageHero = async () => {
    setPageHeroSaving(true);
    try {
      await saveMutualAidPageHero('');
      if (pageHeroPreview?.startsWith?.('blob:')) {
        try { URL.revokeObjectURL(pageHeroPreview); } catch (_) {}
      }
      setPageHeroFile(null);
      setPageHeroPreview('');
      setPageHeroUrl('');
      onNotify?.('Fund page photo removed', 'success');
    } catch (err) {
      onNotify?.(err?.message || 'Could not remove Fund page photo', 'error');
    } finally {
      setPageHeroSaving(false);
    }
  };

  const saveVoting = async () => {
    setReviewBusy('voting');
    try {
      await saveMutualAidVotingPeriod({ votingOpen, votingEndsAt });
      onNotify?.(votingOpen ? 'Voting period is open' : 'Voting period is closed', 'success');
    } catch (err) {
      onNotify?.(err?.message || 'Could not update voting', 'error');
    } finally {
      setReviewBusy(null);
    }
  };

  const reviewSuggestion = async (id, status) => {
    setReviewBusy(id);
    try {
      await setMutualAidSuggestionStatus(id, status);
      onNotify?.(status === 'approved' ? 'Donee approved for voting' : 'Suggestion denied', 'success');
    } catch (err) {
      onNotify?.(err?.message || 'Could not update suggestion', 'error');
    } finally {
      setReviewBusy(null);
    }
  };

  const pickWinner = async (id) => {
    setReviewBusy(id);
    try {
      await selectMutualAidWinner(id);
      onNotify?.('Winner selected and set as the active fund', 'success');
      await reload();
    } catch (err) {
      onNotify?.(err?.message || 'Could not select winner', 'error');
    } finally {
      setReviewBusy(null);
    }
  };

  const coverSrc = draft.coverPreview || draft.coverImageUrl || '';
  const pageHeroSrc = pageHeroPreview || pageHeroUrl || '';
  const metrics = summarizeMutualAidAdmin({ suggestions, funds: rows, page });

  return (
    <View style={styles.wrap}>
      <View style={styles.form}>
        <Text style={styles.sectionTitle}>Fund page photo</Text>
        <Text style={styles.help}>
          This image sits in the center of the Mutual Aid Fund page. Members see it instead of the long explainer text.
        </Text>
        {pageHeroSrc ? (
          <View style={styles.coverPreviewWrap}>
            <Image source={{ uri: pageHeroSrc }} style={styles.pageHeroPreview} resizeMode="cover" />
          </View>
        ) : (
          <Text style={styles.empty}>No page photo yet.</Text>
        )}
        {Platform.OS === 'web' ? (
          <>
            <input
              ref={pageHeroInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={pickPageHero}
            />
            <View style={styles.row}>
              <TouchableOpacity
                onPress={() => pageHeroInputRef.current?.click?.()}
                disabled={pageHeroSaving}
                style={[styles.coverPickBtn, pageHeroSaving && { opacity: 0.6 }]}
              >
                <Text style={styles.coverPickText}>
                  {pageHeroSaving ? 'Uploading…' : (pageHeroSrc ? 'Change page photo' : 'Upload page photo')}
                </Text>
              </TouchableOpacity>
              {pageHeroSrc ? (
                <TouchableOpacity
                  style={[styles.clearTextBtn, pageHeroSaving && { opacity: 0.6 }]}
                  onPress={clearPageHero}
                  disabled={pageHeroSaving}
                >
                  <Text style={styles.clearText}>Remove</Text>
                </TouchableOpacity>
              ) : null}
            </View>
          </>
        ) : null}
      </View>

      <View style={styles.metrics}>
        {[
          { label: 'Money in', value: formatFundsCents(metrics.raisedCents) },
          { label: 'Pending', value: String(metrics.pending) },
          { label: 'Approved', value: String(metrics.approved) },
          { label: 'Votes', value: String(metrics.voteTotal) },
          { label: 'Voting', value: metrics.votingOpen ? 'Open' : 'Closed' },
        ].map((item) => (
          <View key={item.label} style={styles.metric}>
            <Text style={styles.metricValue}>{item.value}</Text>
            <Text style={styles.metricLabel}>{item.label}</Text>
          </View>
        ))}
      </View>

      <View style={styles.form}>
        <Text style={styles.sectionTitle}>Voting period</Text>
        <Text style={styles.help}>
          Approved donees only appear on the Fund page while voting is open. Close it when you pick a winner.
        </Text>
        <View style={styles.row}>
          <Text style={styles.label}>Open for votes</Text>
          <Switch value={votingOpen} onValueChange={setVotingOpen} {...toggleColors} />
          <Text style={styles.label}>Ends</Text>
          <TextInput
            style={[styles.input, { minWidth: 180 }]}
            placeholder="2026-10-01"
            placeholderTextColor={colors.inputPlaceholder}
            value={votingEndsAt}
            onChangeText={setVotingEndsAt}
          />
          <TouchableOpacity
            style={[styles.btn, reviewBusy === 'voting' && { opacity: 0.6 }]}
            onPress={saveVoting}
            disabled={reviewBusy === 'voting'}
          >
            <Text style={styles.btnText}>{reviewBusy === 'voting' ? 'Saving…' : 'Save voting'}</Text>
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.form}>
        <Text style={styles.sectionTitle}>Suggested donees</Text>
        <Text style={styles.help}>
          Approve a suggestion to put it on the public vote. Deny hides it. Select winner closes voting and makes that donee the active fund.
        </Text>
        {suggestions.length === 0 ? (
          <Text style={styles.empty}>No suggestions yet.</Text>
        ) : suggestions.map((row) => {
          const status = normalizeSuggestionStatus(row.status);
          const card = suggestionDisplayFields(row);
          return (
            <View key={row.id} style={styles.suggestCard}>
              {card.imageUrl ? (
                <Image source={{ uri: card.imageUrl }} style={styles.cardCover} resizeMode="cover" />
              ) : null}
              <View style={{ flex: 1 }}>
                <Text style={styles.cardTitle}>{card.title}</Text>
                {card.name && card.name !== card.title ? (
                  <Text style={styles.cardMeta}>{card.name}</Text>
                ) : null}
                <Text style={styles.cardMeta}>
                  {status} · {row.voteCount || 0} vote{(row.voteCount || 0) === 1 ? '' : 's'}
                  {row.userEmail ? ` · ${row.userEmail}` : ''}
                </Text>
                {card.description ? (
                  <Text style={styles.cardDesc} numberOfLines={3}>{card.description}</Text>
                ) : null}
                {card.link ? <Text style={styles.cardDesc}>{card.link}</Text> : null}
                {card.contact ? <Text style={styles.cardMeta}>{card.contact}</Text> : null}
                <View style={styles.row}>
                  {status === 'pending' || status === 'denied' ? (
                    <TouchableOpacity
                      style={[styles.btn, styles.approveBtn, reviewBusy === row.id && { opacity: 0.6 }]}
                      onPress={() => reviewSuggestion(row.id, 'approved')}
                      disabled={Boolean(reviewBusy)}
                    >
                      <Text style={styles.btnText}>Approve</Text>
                    </TouchableOpacity>
                  ) : null}
                  {status === 'pending' || status === 'approved' ? (
                    <TouchableOpacity
                      style={styles.clearTextBtn}
                      onPress={() => reviewSuggestion(row.id, 'denied')}
                      disabled={Boolean(reviewBusy)}
                    >
                      <Text style={styles.clearText}>Deny</Text>
                    </TouchableOpacity>
                  ) : null}
                  {status === 'approved' ? (
                    <TouchableOpacity
                      style={[styles.btn, reviewBusy === row.id && { opacity: 0.6 }]}
                      onPress={() => pickWinner(row.id)}
                      disabled={Boolean(reviewBusy)}
                    >
                      <Text style={styles.btnText}>Select winner</Text>
                    </TouchableOpacity>
                  ) : null}
                  {status === 'winner' ? (
                    <Text style={styles.winnerTag}>Winner</Text>
                  ) : null}
                </View>
              </View>
            </View>
          );
        })}
      </View>

      <Text style={styles.help}>
        Active funds appear on the Fund page. Credits are separate and apply to Map + Calendar subscriptions.
      </Text>

      <View style={styles.form}>
        <Text style={styles.sectionTitle}>Add a cause</Text>
        <TextInput
          style={styles.input}
          placeholder="Fund title"
          placeholderTextColor={colors.inputPlaceholder}
          value={draft.title}
          onChangeText={(title) => setDraft((p) => ({ ...p, title }))}
        />
        <TextInput
          style={[styles.input, styles.textarea]}
          placeholder="Description"
          placeholderTextColor={colors.inputPlaceholder}
          value={draft.description}
          onChangeText={(description) => setDraft((p) => ({ ...p, description }))}
          multiline
        />

        <Text style={styles.label}>Cover photo (optional)</Text>
        {coverSrc ? (
          <View style={styles.coverPreviewWrap}>
            <Image source={{ uri: coverSrc }} style={styles.coverPreview} resizeMode="cover" />
            <TouchableOpacity style={styles.clearCoverBtn} onPress={clearCover}>
              <Text style={styles.clearCoverText}>Remove</Text>
            </TouchableOpacity>
          </View>
        ) : null}
        {Platform.OS === 'web' ? (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={pickCover}
            />
            <TouchableOpacity
              style={styles.coverPickBtn}
              onPress={() => fileInputRef.current?.click?.()}
            >
              <Text style={styles.coverPickText}>
                {coverSrc ? 'Change cover photo' : 'Upload cover photo'}
              </Text>
            </TouchableOpacity>
          </>
        ) : null}

        <View style={styles.row}>
          <Text style={styles.label}>Goal $</Text>
          <TextInput
            style={[styles.input, { flex: 1 }]}
            value={draft.goalDollars}
            placeholderTextColor={colors.inputPlaceholder}
            onChangeText={(goalDollars) => setDraft((p) => ({ ...p, goalDollars }))}
            keyboardType="decimal-pad"
          />
          <Text style={styles.label}>Active</Text>
          <Switch
            value={draft.active}
            onValueChange={(active) => setDraft((p) => ({ ...p, active }))}
            {...toggleColors}
          />
          <TouchableOpacity
            style={[styles.btn, saving && { opacity: 0.6 }]}
            onPress={createFund}
            disabled={saving}
          >
            <Text style={styles.btnText}>{saving ? 'Saving…' : 'Add fund'}</Text>
          </TouchableOpacity>
        </View>
      </View>

      {loading ? (
        <ActivityIndicator color={colors.statAccent} />
      ) : rows.length === 0 ? (
        <Text style={styles.empty}>No mutual funds yet.</Text>
      ) : (
        rows.map((fund) => (
          <View key={fund.id} style={styles.card}>
            {fund.coverImageUrl ? (
              <Image
                source={{ uri: fund.coverImageUrl }}
                style={styles.cardCover}
                resizeMode="cover"
              />
            ) : null}
            <View style={{ flex: 1 }}>
              <Text style={styles.cardTitle}>{fund.title}</Text>
              <Text style={styles.cardMeta}>
                {formatFundsCents(fund.raisedCents)} raised
                {fund.goalCents ? ` / ${formatFundsCents(fund.goalCents)} goal` : ''}
                {fund.active ? ' · Active' : ' · Inactive'}
              </Text>
              {fund.description ? (
                <Text style={styles.cardDesc} numberOfLines={2}>{fund.description}</Text>
              ) : null}
            </View>
            <Switch value={Boolean(fund.active)} onValueChange={() => toggleActive(fund)} {...toggleColors} />
          </View>
        ))
      )}
    </View>
  );
}

function createMutualFundAdminStyles(colors, isDarkMode) {
  return StyleSheet.create({
    wrap: { gap: 12, marginBottom: 24 },
    help: { fontSize: 13, color: colors.textSecondary, lineHeight: 18 },
    sectionTitle: { fontSize: 15, fontWeight: '700', color: colors.textPrimary },
    metrics: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
    metric: {
      minWidth: 110,
      flexGrow: 1,
      padding: 12,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: colors.cardBorder,
      backgroundColor: colors.cardBg,
    },
    metricValue: { fontSize: 20, fontWeight: '800', color: colors.textPrimary },
    metricLabel: { fontSize: 11, color: colors.textMuted, marginTop: 2 },
    suggestCard: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 12,
      padding: 10,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.cardBorder,
      backgroundColor: colors.cardBgSubtle,
    },
    approveBtn: { backgroundColor: colors.accentGreen },
    winnerTag: { fontSize: 12, fontWeight: '700', color: isDarkMode ? '#e4c04a' : '#854d0e' },
    pageHeroPreview: {
      width: '100%',
      height: 200,
      backgroundColor: colors.cardBgSubtle,
    },
    clearTextBtn: { paddingHorizontal: 8, paddingVertical: 8 },
    clearText: { fontSize: 12, fontWeight: '600', color: isDarkMode ? '#f97066' : '#b42318' },
    form: {
      gap: 8,
      padding: 12,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: colors.cardBorder,
      backgroundColor: colors.cardBg,
    },
    input: {
      borderWidth: 1,
      borderColor: colors.inputBorder,
      borderRadius: 8,
      paddingHorizontal: 10,
      paddingVertical: 8,
      fontSize: 13,
      backgroundColor: colors.inputBg,
      color: colors.inputText,
    },
    textarea: { minHeight: 64, textAlignVertical: 'top' },
    row: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
    label: { fontSize: 12, fontWeight: '600', color: colors.textSecondary },
    coverPreviewWrap: {
      position: 'relative',
      borderRadius: 10,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: colors.cardBorder,
    },
    coverPreview: {
      width: '100%',
      height: 140,
      backgroundColor: colors.cardBgSubtle,
    },
    clearCoverBtn: {
      position: 'absolute',
      top: 8,
      right: 8,
      backgroundColor: 'rgba(0,0,0,0.65)',
      borderRadius: 6,
      paddingHorizontal: 10,
      paddingVertical: 5,
    },
    clearCoverText: { color: '#fff', fontSize: 11, fontWeight: '700' },
    coverPickBtn: {
      alignSelf: 'flex-start',
      borderWidth: 1,
      borderColor: colors.cardBorder,
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 8,
      backgroundColor: colors.cardBgSubtle,
    },
    coverPickText: { fontSize: 12, fontWeight: '600', color: colors.textPrimary },
    btn: {
      backgroundColor: '#e67e22',
      borderRadius: 8,
      paddingHorizontal: 14,
      paddingVertical: 9,
    },
    btnText: { color: colors.textOnAccent, fontWeight: '700', fontSize: 12 },
    empty: { fontSize: 13, color: colors.textMuted },
    card: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      padding: 12,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: colors.cardBorder,
      backgroundColor: colors.cardBg,
    },
    cardCover: {
      width: 64,
      height: 64,
      borderRadius: 8,
      backgroundColor: colors.cardBgSubtle,
    },
    cardTitle: { fontSize: 14, fontWeight: '700', color: colors.textPrimary },
    cardMeta: { fontSize: 11, color: colors.textMuted, marginTop: 2 },
    cardDesc: { fontSize: 12, color: colors.textSecondary, marginTop: 4 },
  });
}
